import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Check,
  Circle,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import type {
  EstadoAsamblea,
  AgregarMateriaRequest,
  AnularAsambleaRequest,
  ExtenderVotacionRequest,
} from '@condo/contracts';
import {
  AgregarMateriaRequestSchema,
  AnularAsambleaRequestSchema,
  ExtenderVotacionRequestSchema,
} from '@condo/contracts';
import { useCondoContext } from '@/features/auth/use-condo-context';
import {
  useAsamblea,
  useAgregarMateria,
  useQuitarMateria,
  usePublicarAsamblea,
  useCerrarAsamblea,
  useExtenderVotacion,
  useAnularAsamblea,
  useDeclararSegundaCitacion,
} from './api';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatLongDate, formatShortDate } from '@/lib/format/date';

// ─────────────────────────────────────────────────────────────────────────────
// Estado timeline
// ─────────────────────────────────────────────────────────────────────────────

const TIMELINE: EstadoAsamblea[] = [
  'borrador',
  'convocada',
  'en_votacion',
  'cerrada',
];

const ESTADO_LABELS: Record<EstadoAsamblea, string> = {
  borrador: 'Borrador',
  convocada: 'Convocada',
  en_votacion: 'En votacion',
  sin_quorum: 'Sin quorum',
  cerrada: 'Cerrada',
  cerrada_sin_efecto: 'Cerrada sin efecto',
  anulada: 'Anulada',
};

const VARIANT_MAP: Record<EstadoAsamblea, 'neutral' | 'primary' | 'warning' | 'success' | 'danger'> = {
  borrador: 'neutral',
  convocada: 'primary',
  en_votacion: 'warning',
  sin_quorum: 'danger',
  cerrada: 'success',
  cerrada_sin_efecto: 'neutral',
  anulada: 'neutral',
};

function EstadoTimeline({ estado }: { estado: EstadoAsamblea }) {
  const currentIdx = TIMELINE.indexOf(estado);
  return (
    <div className="flex items-center gap-0">
      {TIMELINE.map((e, idx) => {
        const isPast = currentIdx > idx;
        const isCurrent = currentIdx === idx;
        return (
          <div key={e} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors ${
                  isPast
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-paper)]'
                    : isCurrent
                      ? 'border-[var(--color-primary)] bg-[var(--color-paper)] text-[var(--color-primary)]'
                      : 'border-[var(--color-hairline)] bg-[var(--color-paper-dim)] text-[var(--color-ink-faint)]'
                }`}
              >
                {isPast ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Circle className="h-2 w-2 fill-current" />
                )}
              </div>
              <span
                className={`whitespace-nowrap text-[0.625rem] uppercase tracking-[0.1em] ${
                  isCurrent
                    ? 'font-semibold text-[var(--color-primary)]'
                    : 'text-[var(--color-ink-faint)]'
                }`}
              >
                {ESTADO_LABELS[e]}
              </span>
            </div>
            {idx < TIMELINE.length - 1 && (
              <div
                className={`mx-1 h-0.5 w-8 ${
                  isPast
                    ? 'bg-[var(--color-primary)]'
                    : 'bg-[var(--color-hairline)]'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Agregar materia sub-form
// ─────────────────────────────────────────────────────────────────────────────

type AgregarMateriaInput = z.input<typeof AgregarMateriaRequestSchema>;

const CATEGORIAS = [
  { value: 'informativa', label: 'Informativa' },
  { value: 'gestion_simple', label: 'Gestion simple' },
  { value: 'aprobacion_gasto', label: 'Aprobacion de gasto' },
  { value: 'obra_mayor', label: 'Obra mayor' },
  { value: 'modificacion_reglamento', label: 'Modificacion de reglamento' },
  { value: 'cambio_destino', label: 'Cambio de destino' },
  { value: 'eleccion_cargos', label: 'Eleccion de cargos' },
] as const;

function AgregarMateriaForm({
  condoId,
  asambleaId,
  onDone,
}: {
  condoId: string;
  asambleaId: string;
  onDone: () => void;
}) {
  const mutation = useAgregarMateria(condoId, asambleaId);
  const form = useForm<AgregarMateriaInput, unknown, AgregarMateriaRequest>({
    resolver: zodResolver(AgregarMateriaRequestSchema),
    defaultValues: {
      titulo: '',
      categoria: 'gestion_simple',
      tipoVoto: 'si_no',
    },
  });

  async function onSubmit(values: AgregarMateriaRequest) {
    try {
      await mutation.mutateAsync(values);
      toast.success('Materia agregada');
      form.reset();
      onDone();
    } catch {
      toast.error('No se pudo agregar la materia');
    }
  }

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="mt-4 space-y-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-4"
    >
      <p className="text-sm font-semibold text-[var(--color-ink)]">Nueva materia</p>
      <div>
        <Label className="text-xs">Titulo</Label>
        <Input
          placeholder="Aprobacion presupuesto 2027"
          className="mt-1 h-8 text-sm"
          {...form.register('titulo')}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Categoria</Label>
          <Select
            value={form.watch('categoria')}
            onValueChange={(v) =>
              form.setValue('categoria', v as AgregarMateriaRequest['categoria'], {
                shouldValidate: true,
              })
            }
          >
            <SelectTrigger className="mt-1 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIAS.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Tipo de voto</Label>
          <Select
            value={form.watch('tipoVoto')}
            onValueChange={(v) =>
              form.setValue('tipoVoto', v as AgregarMateriaRequest['tipoVoto'], {
                shouldValidate: true,
              })
            }
          >
            <SelectTrigger className="mt-1 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="si_no">Si / No</SelectItem>
              <SelectItem value="opciones_multiples">Opciones multiples</SelectItem>
              <SelectItem value="eleccion_cargos">Eleccion de cargos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancelar
        </Button>
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Agregar'}
        </Button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Extender votacion form
// ─────────────────────────────────────────────────────────────────────────────

type ExtenderInput = z.input<typeof ExtenderVotacionRequestSchema>;

function ExtenderVotacionDialog({
  condoId,
  asambleaId,
  open,
  onOpenChange,
}: {
  condoId: string;
  asambleaId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const mutation = useExtenderVotacion(condoId);
  const form = useForm<ExtenderInput, unknown, ExtenderVotacionRequest>({
    resolver: zodResolver(ExtenderVotacionRequestSchema),
    defaultValues: { nuevoFin: '' },
  });

  async function onSubmit(values: ExtenderVotacionRequest) {
    try {
      await mutation.mutateAsync({ asambleaId, ...values });
      toast.success('Votacion extendida');
      onOpenChange(false);
      form.reset();
    } catch {
      toast.error('No se pudo extender la votacion');
    }
  }

  if (!open) return null;

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="mt-4 space-y-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-4"
    >
      <p className="text-sm font-semibold">Extender plazo de votacion</p>
      <div>
        <Label className="text-xs">Nuevo fin de votacion</Label>
        <Input type="datetime-local" className="mt-1 h-8 text-sm" {...form.register('nuevoFin')} />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Extender'}
        </Button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Anular dialog
// ─────────────────────────────────────────────────────────────────────────────

type AnularInput = z.input<typeof AnularAsambleaRequestSchema>;

function AnularAsambleaModal({
  condoId,
  asambleaId,
  open,
  onOpenChange,
}: {
  condoId: string;
  asambleaId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const mutation = useAnularAsamblea(condoId);
  const form = useForm<AnularInput, unknown, AnularAsambleaRequest>({
    resolver: zodResolver(AnularAsambleaRequestSchema),
    defaultValues: { razon: '' },
  });

  async function onSubmit(values: AnularAsambleaRequest) {
    try {
      await mutation.mutateAsync({ asambleaId, ...values });
      toast.success('Asamblea anulada');
      onOpenChange(false);
    } catch {
      toast.error('No se pudo anular la asamblea');
    }
  }

  if (!open) return null;

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="mt-4 space-y-3 rounded-md border border-[var(--color-danger)] bg-[var(--color-paper-dim)] p-4"
    >
      <p className="text-sm font-semibold text-[var(--color-danger)]">Anular asamblea</p>
      <div>
        <Label className="text-xs">Razon (minimo 10 caracteres)</Label>
        <Input
          placeholder="Motivo de la anulacion..."
          className="mt-1 h-8 text-sm"
          {...form.register('razon')}
        />
        {form.formState.errors.razon && (
          <p className="mt-1 text-xs text-[var(--color-danger)]">
            {form.formState.errors.razon.message}
          </p>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button type="submit" variant="danger" size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Anular'}
        </Button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────

export function AdminAsambleaPanel({ asambleaId }: { asambleaId: string }) {
  const { condoId } = useCondoContext();
  const { data: asamblea, isLoading } = useAsamblea(condoId, asambleaId);

  const cerrarMutation = useCerrarAsamblea(condoId);
  const publicarMutation = usePublicarAsamblea(condoId);
  const quitarMateriaMutation = useQuitarMateria(condoId, asambleaId);
  const segundaCitacionMutation = useDeclararSegundaCitacion(condoId);

  const [showAddMateria, setShowAddMateria] = useState(false);
  const [showExtender, setShowExtender] = useState(false);
  const [showAnular, setShowAnular] = useState(false);
  const [confirmPublicar, setConfirmPublicar] = useState(false);
  const [confirmCerrar, setConfirmCerrar] = useState(false);
  const [confirmSegunda, setConfirmSegunda] = useState(false);

  if (isLoading || !asamblea) {
    return (
      <PageContainer width="medium">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64" />
      </PageContainer>
    );
  }

  const { estado, materias } = asamblea;

  async function handlePublicar() {
    try {
      await publicarMutation.mutateAsync({ asambleaId });
      toast.success('Asamblea publicada — copropietarios notificados');
    } catch {
      toast.error('No se pudo publicar la asamblea');
    }
  }

  async function handleCerrar() {
    try {
      await cerrarMutation.mutateAsync(asambleaId);
      toast.success('Votacion cerrada manualmente');
    } catch {
      toast.error('No se pudo cerrar la asamblea');
    }
  }

  async function handleSegundaCitacion() {
    try {
      await segundaCitacionMutation.mutateAsync(asambleaId);
      toast.success('Segunda citacion declarada');
    } catch {
      toast.error('No se pudo declarar segunda citacion');
    }
  }

  async function handleQuitarMateria(materiaId: string) {
    try {
      await quitarMateriaMutation.mutateAsync(materiaId);
      toast.success('Materia quitada');
    } catch {
      toast.error('No se pudo quitar la materia');
    }
  }

  return (
    <PageContainer width="medium">
      <Button variant="ghost" asChild size="sm" className="mb-4">
        <Link to={`/app/condominios/${condoId}/asambleas`}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>
      </Button>

      <PageHeader
        eyebrow={`Asamblea ${asamblea.tipo} · Citacion ${asamblea.numeroCitacion}`}
        title={formatLongDate(asamblea.fechaProgramada)}
        subtitle={asamblea.lugar}
        meta={
          <div className="mt-2">
            <Badge variant={VARIANT_MAP[estado]}>{ESTADO_LABELS[estado]}</Badge>
          </div>
        }
        ruleSpacing="mb-8"
      />

      {/* Estado timeline */}
      {!['sin_quorum', 'cerrada_sin_efecto', 'anulada'].includes(estado) && (
        <div className="mb-8 overflow-x-auto">
          <EstadoTimeline estado={estado} />
        </div>
      )}

      {/* Stats */}
      <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Materias" value={String(materias.length)} />
        <StatCard
          label="Votacion desde"
          value={formatShortDate(asamblea.plazoVotacionDesde)}
        />
        <StatCard
          label="Votacion hasta"
          value={formatShortDate(asamblea.plazoVotacionHasta)}
        />
        <StatCard
          label="Quorum requerido"
          value={`${(asamblea.quorumConstitucionRequerido * 100).toFixed(0)}%`}
        />
      </div>

      {/* Materias */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">Orden del dia</h2>
          {estado === 'borrador' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddMateria((v) => !v)}
            >
              <Plus className="h-4 w-4" />
              Agregar materia
            </Button>
          )}
        </div>

        {showAddMateria && estado === 'borrador' && (
          <AgregarMateriaForm
            condoId={condoId}
            asambleaId={asambleaId}
            onDone={() => setShowAddMateria(false)}
          />
        )}

        {materias.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--color-ink-muted)]">
            Sin materias. Agrega al menos una para publicar.
          </p>
        ) : (
          <ol className="mt-4 space-y-2">
            {materias.map((m) => (
              <li
                key={m.id}
                className="flex items-start gap-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-4"
              >
                <span className="font-display text-sm font-semibold text-[var(--color-ink-muted)]">
                  {String(m.orden + 1).padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-sm font-semibold truncate">{m.titulo}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Badge variant="neutral" className="text-[0.625rem]">
                      {m.categoria.replace('_', ' ')}
                    </Badge>
                    <Badge variant="accent" className="text-[0.625rem]">
                      {m.tipoVoto === 'si_no'
                        ? 'Si / No'
                        : m.tipoVoto === 'opciones_multiples'
                          ? 'Opciones'
                          : 'Eleccion cargos'}
                    </Badge>
                    {m.mayoriaRequerida && (
                      <Badge variant="primary" className="text-[0.625rem]">
                        {m.mayoriaRequerida.replace('_', ' ')}
                      </Badge>
                    )}
                  </div>
                </div>
                {estado === 'borrador' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => handleQuitarMateria(m.id)}
                    aria-label="Quitar materia"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Acciones contextuales */}
      <div className="space-y-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-5">
        <p className="eyebrow">Acciones</p>

        {estado === 'borrador' && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={materias.length === 0 || publicarMutation.isPending}
              onClick={() => setConfirmPublicar(true)}
            >
              {publicarMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Publicar convocatoria
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowAnular(true)}
            >
              Anular
            </Button>
          </div>
        )}

        {estado === 'convocada' && (
          <div className="flex flex-wrap gap-2">
            <p className="w-full text-sm text-[var(--color-ink-muted)]">
              La asamblea esta convocada. La votacion se abre segun el plazo configurado.
            </p>
            <Button variant="danger" size="sm" onClick={() => setShowAnular(true)}>
              Anular
            </Button>
          </div>
        )}

        {estado === 'en_votacion' && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setConfirmCerrar(true)}
              disabled={cerrarMutation.isPending}
            >
              {cerrarMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Cerrar votacion
            </Button>
          </div>
        )}

        {estado === 'sin_quorum' && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowExtender(true)}>
              Extender plazo
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setConfirmSegunda(true)}
              disabled={segundaCitacionMutation.isPending}
            >
              {segundaCitacionMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Declarar segunda citacion
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowAnular(true)}
            >
              Cerrar sin efecto
            </Button>
          </div>
        )}

        {(estado === 'cerrada' || estado === 'cerrada_sin_efecto') && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/app/condominios/${condoId}/asambleas/${asambleaId}/acta`}>
                <FileText className="h-4 w-4" />
                Ver acta
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/app/condominios/${condoId}/asambleas/${asambleaId}/acta`}>
                <ExternalLink className="h-4 w-4" />
                Resultados
              </Link>
            </Button>
          </div>
        )}

        {/* Inline forms */}
        {showExtender && (
          <ExtenderVotacionDialog
            condoId={condoId}
            asambleaId={asambleaId}
            open={showExtender}
            onOpenChange={setShowExtender}
          />
        )}

        {showAnular && (
          <AnularAsambleaModal
            condoId={condoId}
            asambleaId={asambleaId}
            open={showAnular}
            onOpenChange={setShowAnular}
          />
        )}
      </div>

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirmPublicar}
        onOpenChange={setConfirmPublicar}
        title="Publicar convocatoria"
        description="Se notificara a todos los copropietarios. Esta accion no se puede deshacer (solo anular)."
        confirmLabel="Publicar"
        onConfirm={() => {
          setConfirmPublicar(false);
          void handlePublicar();
        }}
        loading={publicarMutation.isPending}
      />

      <ConfirmDialog
        open={confirmCerrar}
        onOpenChange={setConfirmCerrar}
        title="Cerrar votacion"
        description="Se cerrara la votacion y se calcularan los resultados. Esta accion es irreversible."
        confirmLabel="Cerrar votacion"
        onConfirm={() => {
          setConfirmCerrar(false);
          void handleCerrar();
        }}
        loading={cerrarMutation.isPending}
      />

      <ConfirmDialog
        open={confirmSegunda}
        onOpenChange={setConfirmSegunda}
        title="Declarar segunda citacion"
        description="Se creara una nueva asamblea en segunda citacion. El quorum requerido se reduce segun la Ley 21.442."
        confirmLabel="Declarar segunda citacion"
        onConfirm={() => {
          setConfirmSegunda(false);
          void handleSegundaCitacion();
        }}
        loading={segundaCitacionMutation.isPending}
      />
    </PageContainer>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-4">
      <p className="eyebrow text-[0.6rem]">{label}</p>
      <p className="font-display-tight mt-1 text-xl font-semibold tabular">{value}</p>
    </div>
  );
}
